import * as PIXI from 'pixi.js';
import { Config } from '../video-data/config';
import { assertArray, assertGreaterThan, assertNumber } from '../validators';
import { UpdateFlowMixin } from '../core/updateFlowMixin';
import { ParentMixin } from '../core/parentMixin';
import { SerializableMixin } from '../core/serializableMixin';

/**
 * TODO - should we refactor to a more standard luminary object instead of extending from
 *  PIXI.Matrix? Use attributes and the same serialization pattern
 * PIXI.TransformMatrix override to unify the behavior of getting and setting array data.
 */
export class TransformMatrix extends UpdateFlowMixin(ParentMixin(SerializableMixin(PIXI.Matrix))) {
    /**
     * Returns the internal matrix as an Array
     * @returns an array containing 6 numbers for the following properties [ x scale, x skew, x translate,
     * y scale, y skew, y translate].
     */
    override save(): number[] {
        return [
            this.a, // x scale
            this.b, // x skew
            this.tx, // x translation
            this.d, // y scale
            this.c, // y skew
            this.ty, // y translation
        ];
    }

    /**
     * Applies the given array
     * @param value - an array containing 6 numbers for the following properties [ x scale, x skew, x translate,
     * y scale, y skew, y translate].
     */
    override load(value: number[]) {
        assertArray(value);
        if (value.length !== 6) {
            throw Error(`Expected array to be length 6, got ${value.length} instead.`);
        }
        if (this.isEqual(value)) {
            return;
        }
        this.a = value[0];
        this.b = value[1];
        this.tx = value[2];
        this.d = value[3];
        this.c = value[4];
        this.ty = value[5];
        this.requiresUpdate = true;
        return this;
    }

    /**
     * Compare the internal state to an array to determine if they are equivalent
     * representations of a transform
     */
    isEqual(compareTo: Array<number>) {
        if (compareTo.length !== 6) {
            return false;
        }
        return (
            this.a === compareTo[0] &&
            this.b === compareTo[1] &&
            this.tx === compareTo[2] &&
            this.d === compareTo[3] &&
            this.c === compareTo[4] &&
            this.ty === compareTo[5]
        );
    }

    set xTranslation(val: number) {
        if (this.tx === val) {
            return;
        }
        assertNumber(val);
        this.tx = val;
        this.requiresUpdate = true;
    }

    get xTranslation(): number {
        return this.tx;
    }

    set yTranslation(val: number) {
        if (this.ty === val) {
            return;
        }
        assertNumber(val);
        this.ty = val;
        this.requiresUpdate = true;
    }

    get yTranslation(): number {
        return this.ty;
    }

    set scaleX(val: number) {
        if (this.a === val) {
            return;
        }
        assertNumber(val);
        assertGreaterThan(val, Config.MEDIA_MIN_SCALE_X);
        this.a = val;
        this.requiresUpdate = true;
    }

    get scaleX(): number {
        return this.a;
    }

    set scaleY(val: number) {
        if (this.d === val) {
            return;
        }
        assertNumber(val);
        assertGreaterThan(val, Config.MEDIA_MIN_SCALE_Y);
        this.d = val;
        this.requiresUpdate = true;
    }

    get scaleY(): number {
        return this.d;
    }

    reset() {
        this.load([1, 0, 0, 1, 0, 0]);
    }
}
